import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  ArrowRight, 
  Leaf,
  Recycle,
  Shield,
  Heart,
  Award,
  Clock,
  Users,
  ChevronLeft
} from "lucide-react";
import axios from "axios";
import { PublicNav } from "@/components/public/PublicNav";
import { PublicFooter } from "@/components/public/PublicFooter";
import { usePublicData } from "@/hooks/usePublicData";
import { LOGO_URL, SITE_ORIGIN, DEFAULT_PHONE, DEFAULT_EMAIL } from "@/lib/publicSite";
import { useSeoHeadTags } from "@/hooks/useSeoHeadTags";

const API = `${window.location.origin}/api`;

const values = [
  {
    icon: Leaf,
    title: "Sustainability First",
    description: "We prioritise reclaimed and sustainably sourced materials in every project, reducing environmental impact while creating beautiful results."
  },
  {
    icon: Recycle,
    title: "Waste Reduction",
    description: "Our commitment to minimal waste means careful material planning, proper recycling, and responsible disposal on every job."
  },
  {
    icon: Shield,
    title: "Quality Guaranteed",
    description: "Every piece of work is completed to the highest standard, backed by our satisfaction guarantee."
  },
  {
    icon: Heart,
    title: "Community Focus",
    description: "Based near Abergavenny, we're proud to serve our local community across South Wales and the Welsh borders."
  }
];

const stats = [
  { value: "5+", label: "Years Experience" },
  { value: "200+", label: "Happy Customers" },
  { value: "20", label: "Mile Service Radius" },
  { value: "5.0", label: "Star Rating" }
];

export default function AboutPage() {
  // Shared cached public data (CMS blocks, business info, services) — no content flash
  const { bizInfo, services, cms, cmsHtml, ready } = usePublicData();

  useSeoHeadTags({
    title: "About Us | Sustainable Handyman Services | Abergavenny Carpenter",
    description: "Meet Sandy, your trusted handyman in Abergavenny. Eco-friendly carpentry, repairs & furniture builds. Quality craftsmanship with sustainable practices in Monmouthshire.",
    canonical: `${SITE_ORIGIN}/about`,
    ogType: "website",
    // Mirror of the landing-page LocalBusiness JSON-LD for consistent NAP signals.
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "@id": `${SITE_ORIGIN}/#localbusiness`,
      "name": "Sustainable Handyman Services",
      "image": LOGO_URL,
      "url": `${SITE_ORIGIN}/about`,
      "telephone": bizInfo?.business_phone || DEFAULT_PHONE,
      "email": bizInfo?.business_email || DEFAULT_EMAIL,
      "priceRange": "££",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Abergavenny",
        "addressRegion": "Monmouthshire",
        "addressCountry": "GB"
      },
      "geo": { "@type": "GeoCoordinates", "latitude": 51.8242, "longitude": -3.0167 },
      "areaServed": [
        { "@type": "City", "name": "Abergavenny" },
        { "@type": "AdministrativeArea", "name": "Monmouthshire" }
      ],
      "serviceType": ["Carpentry", "General Repairs", "Furniture Building", "Fencing", "Garden Buildings"]
    },
  });

  // Track page view on load
  useEffect(() => {
    const trackPageView = async () => {
      try {
        let sessionId = sessionStorage.getItem('shs_session_id');
        if (!sessionId) {
          sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          sessionStorage.setItem('shs_session_id', sessionId);
        }
        
        await axios.post(`${API}/analytics/pageview`, null, {
          params: { page: '/about', session_id: sessionId }
        });
      } catch (error) {}
    };
    trackPageView();
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <PublicNav />

      {/* Hero Section */}
      <section className="pt-24 pb-12 md:pt-32 md:pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back button */}
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-8"
            data-testid="back-to-home"
          >
            <ChevronLeft className="h-4 w-4" />
            <span>Back to Home</span>
          </Link>
          
          <div className="text-center max-w-3xl mx-auto">
            <p className="text-accent font-medium tracking-wide uppercase text-sm mb-2" data-testid="about-hero-headline">{cms("about.hero_headline", "About Us")}</p>
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-foreground font-heading mb-6">
              Meet <span className="text-primary">Sandy &amp; Mark</span>
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed" data-testid="about-hero-subheadline">
              {cms("about.hero_subheadline", "We're a family-run handyman service based near Abergavenny, dedicated to delivering quality craftsmanship with sustainability at our core.")}
            </p>
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight font-heading mb-8 text-center">Our Story</h2>
            
            <div className="text-muted-foreground leading-relaxed mb-6 text-lg prose prose-lg max-w-none" data-testid="about-intro-paragraph" dangerouslySetInnerHTML={cmsHtml("about.intro_paragraph", "<p>Sustainable Handyman Services grew out of a simple idea: do good work, solve real problems properly, and tread lightly while doing it.</p>")} />

            {/* Mission statement — CMS editable, shown when set */}
            {cms("about.mission_statement", null) && (
              <div className="bg-accent/10 border-l-4 border-accent rounded-lg p-4 md:p-5 mb-8 prose prose-sm max-w-none" data-testid="about-mission-statement" dangerouslySetInnerHTML={cmsHtml("about.mission_statement", "")} />
            )}
            
            {/* Sandy's Story */}
            <div className="bg-secondary/30 rounded-2xl p-6 md:p-8 mb-8">
              <p className="text-foreground leading-relaxed mb-4">
                I&apos;m Sandy. Before this business existed, I spent nearly 30 years in engineering, much of that time as a CNC machine programmer. Engineering taught me how to think — how to break problems down, work methodically, and design solutions that actually last.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-4">
                When life shifted and I stepped away from full-time employment, I began helping neighbours with repairs, builds and odd jobs. One job led to another, and Sustainable Handyman Services was born — rooted in honesty, practicality and a strong belief in using materials wisely.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Sustainability isn&apos;t a buzzword for us. It&apos;s about respect — for materials, for craftsmanship, and for people&apos;s homes. Wherever possible, we work with reclaimed timber, repair rather than replace, and use traditional, low-impact methods alongside modern tools. Every job is treated as a problem to be solved thoughtfully, not rushed through.
              </p>
            </div>

            {/* Mark's Story */}
            <div className="mb-8">
              <p className="text-foreground leading-relaxed mb-4 text-lg font-medium">
                Mark joining the business marked a real turning point.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Mark comes from a similar engineering background, specialising in electronics, and shares the same deep problem-solving mindset. Seven years ago, he moved to France where he and his family built a new life from the ground up — quite literally. Mark designed and built a tiny house for his family on land they had bought, applying engineering logic, creativity and self-reliance to every stage. Alongside this, they established a small shop where Mark&apos;s wife sold her crochet work, with Mark designing and making complementary items to support the business.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                When Mark and his family decided to move back to Wales last year, I jumped at the chance to bring him onboard. It has proven to be one of the best decisions I&apos;ve made. Our skills overlap in the best way — two engineers with different specialisms, a shared work ethic, and a calm, methodical approach to complex or unusual jobs.
              </p>
            </div>

            {/* Together */}
            <div className="border-l-4 border-primary pl-6 py-2 mb-8">
              <p className="text-foreground leading-relaxed mb-4">
                Together, we bring clarity to projects that feel overwhelming, creativity to spaces that need rethinking, and craftsmanship to every detail. Whether it&apos;s a small repair, a bespoke build, or a larger transformation, our focus is always the same: practical solutions, honest communication, and work we&apos;re proud to stand behind.
              </p>
              <p className="text-muted-foreground leading-relaxed italic">
                Sustainable Handyman Services is built on trust, skill and care — and we&apos;re glad you&apos;re here to see what we do.
              </p>
            </div>

            {/* 5-Star Rating Badge */}
            <div className="flex items-center justify-center gap-4 pt-4">
              <div className="bg-primary/10 p-3 rounded-full">
                <Award className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="font-semibold text-foreground">5-Star Rated</p>
                <p className="text-sm text-muted-foreground">on Facebook Reviews</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 md:py-16 bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center" data-testid={`stat-${stat.label}`}>
                <p className="text-4xl md:text-5xl font-bold text-white font-heading mb-2">{stat.value}</p>
                <p className="text-white/70 text-sm md:text-base">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-accent font-medium tracking-wide uppercase text-sm mb-2">What Drives Us</p>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight font-heading">Our Values</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <Card 
                key={index}
                className="bg-card border border-border/50 hover:-translate-y-1 transition-transform duration-300"
                data-testid={`value-card-${index}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-primary/10 p-3 rounded-lg shrink-0">
                      <value.icon className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold font-heading mb-2">{value.title}</h3>
                      <p className="text-muted-foreground">{value.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-12 md:py-16 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-accent font-medium tracking-wide uppercase text-sm mb-2">Why Choose Us</p>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight font-heading mb-6">What Sets Us Apart</h2>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="bg-accent/10 p-2 rounded-full shrink-0 mt-1">
                    <Clock className="h-5 w-5 text-accent" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Reliable & Punctual</h4>
                    <p className="text-muted-foreground text-sm">We turn up when we say we will and keep you informed throughout the project.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-accent/10 p-2 rounded-full shrink-0 mt-1">
                    <Users className="h-5 w-5 text-accent" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Personal Service</h4>
                    <p className="text-muted-foreground text-sm">As a small family team, you&apos;ll always deal directly with us - no middlemen or subcontractors.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-accent/10 p-2 rounded-full shrink-0 mt-1">
                    <Leaf className="h-5 w-5 text-accent" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Eco-Conscious</h4>
                    <p className="text-muted-foreground text-sm">We actively source reclaimed materials and implement sustainable practices on every job.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-accent/10 p-2 rounded-full shrink-0 mt-1">
                    <Shield className="h-5 w-5 text-accent" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Fully Insured</h4>
                    <p className="text-muted-foreground text-sm">Complete peace of mind with full public liability insurance on all work.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="aspect-video rounded-2xl overflow-hidden bg-secondary">
              <img 
                src="https://customer-assets.emergentagent.com/job_e63e5c23-c73b-48e4-b5bd-c8da1ff8d651/artifacts/25f9bfvg_IMG_3732.jpeg"
                alt="Custom furniture by Sustainable Handyman Services"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-secondary rounded-3xl p-8 md:p-12 text-center">
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight font-heading mb-4">
              Ready to Start Your Project?
            </h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Get in touch today for a free, no-obligation quote. We&apos;d love to hear about your project!
            </p>
            <Link to="/enquiry" data-testid="cta-enquiry-btn">
              <Button size="lg" className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-6 text-lg font-medium">
                Get a Free Quote
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter bizInfo={bizInfo} services={services} />
    </div>
  );
}
